import { useEffect, useState } from 'react';
import { Alert, Col, ControlLabel, FormGroup, Glyphicon, Modal } from 'react-bootstrap';
import * as DateTimeModule from 'react-datetime';
import * as moment from 'moment';
import {
    DISPLAY_FORMAT,
    FINAL_XM_BUSINESS_DAYS_AFTER_REQUEST,
    FINAL_XM_MIN_DAYS_BEFORE_SURGERY,
    IValidationResult,
    MAX_DAYS_BEFORE_SURGERY,
    MIN_DAYS_BEFORE_SURGERY,
    RELAXED_GATE_DAYS,
    RELAXED_MIN_DAYS_BEFORE_SURGERY,
    STORAGE_FORMAT,
    defaultFinalXmDate,
    defaultSerologyDate,
    isRelaxedWindow,
    validateFinalXmDate,
    validateSerologyDate
} from '@nkr/utils/blood-draw-validation';
import { styles } from './styles';

export interface IAcceptXMDecision {
    requiresFreshBlood?: boolean,
    serologyBloodDrawDate?: string,
    finalXmBloodDrawDate?: string,
    requiresSerology?: boolean
}

interface IProps {
    show: boolean,
    targetSurgeryDate?: string,
    donorCenterId?: number,
    donorRdnCenterId?: number,
    recipientCenterId?: number,
    onSubmitAccept: (initials: string, decision: IAcceptXMDecision) => void,
    onCancel: () => void
}

const DateTime = (DateTimeModule as any).default || DateTimeModule;

const AcceptXMDecision = ({ show, onCancel, recipientCenterId, donorCenterId, donorRdnCenterId, targetSurgeryDate, onSubmitAccept }: IProps) => {

    const [initials, setInitials] = useState('');
    const [requiresSerology, setRequiresSerology] = useState<boolean | undefined>(undefined);
    const [requiresFinalXM, setRequiresFinalXM] = useState<boolean | undefined>(undefined);
    const [serologyBloodDrawDate, setSerologyBloodDrawDate] = useState('');
    const [finalXmBloodDrawDate, setFinalXmBloodDrawDate] = useState('');

    const isInternalCase = recipientCenterId && (
        donorCenterId === recipientCenterId || (donorRdnCenterId != null && donorRdnCenterId === recipientCenterId)
    );

    const relaxedWindow = isRelaxedWindow(targetSurgeryDate);

    useEffect(() => {
        if (show) {
            // Both draws seed to the same day so the coordinator schedules a single visit. The
            // Final XM seed diverges only where that shared date is not legal for it.
            const bloodDrawDate = defaultSerologyDate(targetSurgeryDate);

            setInitials('');
            setRequiresSerology(undefined);
            setRequiresFinalXM(undefined);
            setSerologyBloodDrawDate(bloodDrawDate);
            setFinalXmBloodDrawDate(defaultFinalXmDate(bloodDrawDate, targetSurgeryDate));
        }
    }, [show]);

    const handleDateChange = (value: moment.Moment | string, setDate: (date: string) => void) => {
        if (moment.isMoment(value)) {
            setDate(value.format(STORAGE_FORMAT));
            return;
        }

        const parsed = moment(value, DISPLAY_FORMAT, true);
        setDate(parsed.isValid() ? parsed.format(STORAGE_FORMAT) : '');
    };

    // Non-internal cases always need a serology, so they are not asked and the field always shows.
    const serologyRequired = isInternalCase ? requiresSerology === true : true;

    const serologyValidation = serologyBloodDrawDate
        ? validateSerologyDate(moment(serologyBloodDrawDate, STORAGE_FORMAT), targetSurgeryDate)
        : { isValid: false, error: 'Serology blood draw date is required.' };

    const finalXmValidation = finalXmBloodDrawDate
        ? validateFinalXmDate(moment(finalXmBloodDrawDate, STORAGE_FORMAT), targetSurgeryDate)
        : { isValid: false, error: 'Final XM blood draw date is required.' };

    const isValid = initials.length === 2
        && (!isInternalCase || requiresSerology !== undefined)
        && requiresFinalXM !== undefined
        && (!serologyRequired || serologyValidation.isValid)
        && (!requiresFinalXM || finalXmValidation.isValid);

    const handleSubmit = () => {
        onSubmitAccept(initials, {
            requiresFreshBlood: requiresFinalXM,
            serologyBloodDrawDate: serologyRequired ? serologyBloodDrawDate : undefined,
            finalXmBloodDrawDate: requiresFinalXM ? finalXmBloodDrawDate : undefined,
            requiresSerology: serologyRequired
        });
    };

    const renderStatus = (validation: IValidationResult) => {
        if (validation.isValid) {
            return <span style={styles.statusValid}><Glyphicon glyph='ok' /> Valid date</span>;
        }

        return <span style={styles.statusInvalid}>{validation.error}</span>;
    };

    return (
        <Modal bsSize='lg' show={show} backdrop='static' onHide={onCancel}>
            <Modal.Header closeButton>
                <Modal.Title>Donor Cleared and XM Acceptable</Modal.Title>
            </Modal.Header>
            <Modal.Body>
                {isInternalCase && (
                    <div className='row xm_accepted' style={styles.alertRow}>
                        <Col lg={12}>
                            <Alert bsStyle='info'>
                                <strong>Internal Case:</strong> Donor and recipient are at the same center.
                            </Alert>
                        </Col>
                    </div>
                )}

                {relaxedWindow && (
                    <div className='row xm_accepted' style={styles.alertRow}>
                        <Col lg={12}>
                            <Alert bsStyle='warning'>
                                <strong>Surgery Within {RELAXED_GATE_DAYS} Days:</strong> The standard {MIN_DAYS_BEFORE_SURGERY}-{MAX_DAYS_BEFORE_SURGERY} day blood draw window does not apply. Draws may be scheduled from tomorrow through {RELAXED_MIN_DAYS_BEFORE_SURGERY} days before surgery.
                            </Alert>
                        </Col>
                    </div>
                )}

                {isInternalCase && (
                    <div className='row xm_accepted'>
                        <FormGroup>
                            <Col lg={5} componentClass={ControlLabel}>
                                Does this internal case require a Serology? <sup className='req'>*</sup>
                            </Col>
                            <Col lg={7}>
                                <label style={styles.radioLabel}>
                                    <input
                                        name='requiresSerology'
                                        type='radio'
                                        style={styles.radioInput}
                                        checked={requiresSerology === true}
                                        onChange={() => setRequiresSerology(true)} /> Yes
                                </label>
                                <label>
                                    <input
                                        name='requiresSerology'
                                        type='radio'
                                        style={styles.radioInput}
                                        checked={requiresSerology === false}
                                        onChange={() => setRequiresSerology(false)} /> No
                                </label>
                            </Col>
                        </FormGroup>
                    </div>
                )}

                {serologyRequired && (
                    <div className='row xm_accepted'>
                        <FormGroup validationState={serologyValidation.isValid ? null : 'error'}>
                            <Col lg={5} componentClass={ControlLabel}>
                                Serology Blood Draw Date <sup className='req'>*</sup>
                                <span style={styles.fieldHelp}>
                                    {relaxedWindow
                                        ? `Select a date from tomorrow through ${RELAXED_MIN_DAYS_BEFORE_SURGERY} days prior to donor surgery (Monday - Friday only)`
                                        : `Select a date ${MIN_DAYS_BEFORE_SURGERY}-${MAX_DAYS_BEFORE_SURGERY} days prior to donor surgery (Monday - Friday only)`}
                                </span>
                            </Col>
                            <Col lg={7}>
                                <DateTime
                                    value={serologyBloodDrawDate ? moment(serologyBloodDrawDate, STORAGE_FORMAT) : null}
                                    dateFormat={DISPLAY_FORMAT}
                                    timeFormat={false}
                                    strictParsing={true}
                                    isValidDate={(currentDate: moment.Moment) => validateSerologyDate(currentDate, targetSurgeryDate).isValid}
                                    onChange={(value: moment.Moment | string) => handleDateChange(value, setSerologyBloodDrawDate)}
                                    inputProps={{
                                        className: 'form-control',
                                        placeholder: DISPLAY_FORMAT
                                    }}
                                    closeOnSelect={true}
                                />
                                <div style={styles.statusCell}>
                                    {renderStatus(serologyValidation)}
                                </div>
                            </Col>
                        </FormGroup>
                    </div>
                )}

                <div className='row xm_accepted'>
                    <FormGroup>
                        <Col lg={5} componentClass={ControlLabel}>
                            Does this match require a Fresh Blood Final Cross Match? <sup className='req'>*</sup>
                        </Col>
                        <Col lg={7}>
                            <label style={styles.radioLabel}>
                                <input
                                    name='requiresFreshBlood'
                                    type='radio'
                                    style={styles.radioInput}
                                    checked={requiresFinalXM === true}
                                    onChange={() => setRequiresFinalXM(true)} /> Yes
                            </label>
                            <label>
                                <input
                                    name='requiresFreshBlood'
                                    type='radio'
                                    style={styles.radioInput}
                                    checked={requiresFinalXM === false}
                                    onChange={() => setRequiresFinalXM(false)} /> No
                            </label>
                        </Col>
                    </FormGroup>
                </div>

                {requiresFinalXM && (
                    <div className='row xm_accepted'>
                        <FormGroup validationState={finalXmValidation.isValid ? null : 'error'}>
                            <Col lg={5} componentClass={ControlLabel}>
                                Final XM Blood Draw Date <sup className='req'>*</sup>
                                <span style={styles.fieldHelp}>
                                    {relaxedWindow
                                        ? `Select a date from tomorrow through ${RELAXED_MIN_DAYS_BEFORE_SURGERY} days prior to donor surgery (Monday - Thursday only)`
                                        : `Select a date ${FINAL_XM_BUSINESS_DAYS_AFTER_REQUEST} business days after the request and no less than ${FINAL_XM_MIN_DAYS_BEFORE_SURGERY} days prior to donor surgery (Monday - Thursday only)`}
                                </span>
                            </Col>
                            <Col lg={7}>
                                <DateTime
                                    value={finalXmBloodDrawDate ? moment(finalXmBloodDrawDate, STORAGE_FORMAT) : null}
                                    dateFormat={DISPLAY_FORMAT}
                                    timeFormat={false}
                                    strictParsing={true}
                                    isValidDate={(currentDate: moment.Moment) => validateFinalXmDate(currentDate, targetSurgeryDate).isValid}
                                    onChange={(value: moment.Moment | string) => handleDateChange(value, setFinalXmBloodDrawDate)}
                                    inputProps={{
                                        className: 'form-control',
                                        placeholder: DISPLAY_FORMAT
                                    }}
                                    closeOnSelect={true}
                                />
                                <div style={styles.statusCell}>
                                    {renderStatus(finalXmValidation)}
                                </div>
                            </Col>
                        </FormGroup>
                    </div>
                )}
            </Modal.Body>
            <Modal.Footer>
                <div style={styles.footer}>
                    <FormGroup style={styles.initialsGroup}>
                        <ControlLabel style={styles.initialsLabel}>Your Initials <sup className='req'>*</sup></ControlLabel>
                        <input className='form-control'
                            style={styles.initialsInput}
                            type='text'
                            maxLength={2}
                            value={initials}
                            onChange={(e) => setInitials(e.target.value)}
                        />
                    </FormGroup>
                    <button className='btn btn-primary btn-lg' type='button' onClick={handleSubmit} disabled={!isValid}>Accept</button>
                    <button className='btn btn-lg btn-link' type='button' onClick={onCancel}>Cancel</button>
                </div>
            </Modal.Footer>
        </Modal>
    );
}

export default AcceptXMDecision;
